import React, { useState } from 'react';
import {
  fetchEnd,
  fetchStart,
  Record as RaRecord,
  useAuthProvider,
  useRefresh,
  CRUD_UPDATE_SUCCESS
} from 'react-admin';
import { useDispatch, useStore } from 'react-redux';

import axios, { AxiosError, AxiosResponse } from 'axios';

import {
  BPError,
  BPErrorResponse
} from 'shared/services/backoffice-data-provider';
import { BPApiResponse } from 'shared/services/backoffice-data-provider/models/server-responses/bp-api-response.model';
import { getApiRoot } from 'shared/utils/helperFunctions/dotenv.utils';
import { createSearchParams } from 'shared/utils/navigation';

/**
 * Custom hook for making API calls with axios and syncing with React Admin's Redux store
 * @param props.resource - The API resource path (e.g., 'content/123')
 * @param props.reduxResourceName - Optional. The explicit Redux resource path to update (e.g., 'content/user_activities')
 * @param props.params - Optional. Query parameters for the request
 * @param props.init - Optional. Request initialization options
 * @param props.autoFetch - Optional. Whether to fetch automatically on mount
 */
export function useAxiosCall<Data = RaRecord, Params = object>(props: {
  resource: string;
  reduxResourceName?: string;
  params?: Params;
  init?: RequestInit;
  autoFetch?: boolean;
}) {
  const client = axios.create();
  const { resource, reduxResourceName: explicitReduxResourceName, init, autoFetch = true, params = {} } = props;
  const authProvider = useAuthProvider();
  const dispatch = useDispatch();
  const refresh = useRefresh();
  const store = useStore();
  
  // Extract resource name and ID from the resource string
  const resourceParts = resource.split('/');
  const resourceName = resourceParts[0];
  const resourceId = resourceParts.length > 1 ? resourceParts[1] : undefined;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Data>();
  const [error, setError] = useState<BPError | undefined>();

  const handleFetch = React.useCallback(
    async (_params?: Params) => {
      setLoading(true);
      dispatch(fetchStart());
      const token = await authProvider.getToken();
      const method = init?.method ?? 'GET';
      let url = `${getApiRoot()}/${resource}`;
      let requestData = _params ?? params ?? undefined;
      
      if (method === 'GET') {
        url = `${url}?${createSearchParams(_params ?? params ?? {})}`;
        requestData = undefined;
      }
      
      try {
        const response = await client.request({
          url,
          method,
          data: requestData,
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const { payload } = response.data as BPApiResponse<Data>;
        
        // Special handling for PATCH operations to sync with Redux store
        if (method === 'PATCH' && requestData && resourceId) {
          const state = store.getState();

          // Only use explicitly provided Redux resource name for PATCH operations
          // This ensures we only update Redux when we know exactly where to update
          const reduxResourceName = explicitReduxResourceName;
          
          if (!reduxResourceName) {
            const updatedData = {
              ...data,
              ...requestData as any,
              id: resourceId
            } as Data;
            
            setData(updatedData);
            dispatch(fetchEnd());
            setLoading(false);
            
            // Return the updated data directly
            return updatedData;
          }
          
          const currentState = state?.admin?.resources?.[reduxResourceName]?.data?.[resourceId];
          
          // Create updated record by merging current state with request data
          const updatedRecord = currentState ? {
            ...currentState,
            ...requestData as any
          } : {
            ...requestData as any,
            id: resourceId
          };

          setData(updatedRecord as Data);
          
          // Dispatch Redux action to update the global state
          // This will directly update the record in the Redux store
          dispatch({
            type: CRUD_UPDATE_SUCCESS,
            payload: {
              data: updatedRecord
            },
            meta: {
              resource: reduxResourceName, // Use the corrected resource name
              fetchResponse: 'UPDATE',
              fetchStatus: 'RA/FETCH_END'
            }
          });

          dispatch(fetchEnd());
          setLoading(false);
          return updatedRecord as Data;
        }
        
        // Normal handling for non-PATCH operations
        setData(payload as Data);
        dispatch(fetchEnd());
        setLoading(false);
        return payload as Data;
      } catch (e) {
        const axiosError = e as AxiosError<BPErrorResponse>;
        const error: BPError | undefined = axiosError.response?.data.error;
        setError(error);
        dispatch(fetchEnd());
        setLoading(false);
        throw error;
      }
    },
    [dispatch, authProvider, client, resource, params, init?.method, data, resourceName, resourceId, refresh, store]
  );

  React.useEffect(() => {
    if (autoFetch) handleFetch();
  }, [handleFetch, autoFetch]);
  return React.useMemo(
    () => ({ loading, data, error, fetch: handleFetch }),
    [loading, data, error, handleFetch]
  );
}
